import React, { useContext, useState } from "react"
import { graphql } from "gatsby"
import { VisitorLocaleContext } from "../components/providers/VisitorLocaleProvider"
import Layout from "../components/Layout"
import { Slat } from "../components/template/Slat"
import { Link } from "../components/atoms/Link"
import { useLiveTemplatesData } from "../components/hooks/useLiveTemplateData"
import styled, { css } from "styled-components"
import { Margin, mb, p, Padding } from "styled-components-spacing"
import { Select } from "../components/atoms/FormFields"
import { P } from "../components/atoms/Elements"
import { H1, H3 } from "../components/atoms/Headings"
import SEO from "../components/SEO"
import headerBackground from "../images/header.png"
import breakpoint from "styled-components-breakpoint"
import { Helmet } from "react-helmet"
import { ROW } from "../components/hooks/useAllTemplateRegions"
import { salesforceCountries } from "@karbonhq/marketing-shared-utils/utils/countries"
import { SectionTitle } from "../components/collection/sectionTitle"
import { Button } from "../components/atoms/Buttons"
const LinkResolver = require("../utils/linkResolver")

const Header = styled.header`
  background: ${props => props.theme.colors.coral} url("${headerBackground}")
    50% 50%;
  background-size: cover;
  border-radius: 4px;
  color: white;
  min-height: 230px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${mb(3)};
  ${p(2)};
  text-align: center;
`

const TemplateListPage = props => {
  const { regionFilter, setRegionFilter } = useContext(VisitorLocaleContext)
  const liveTemplates = useLiveTemplatesData()

  let templates = [...props.data.allContentfulTemplate.nodes]
  const SORT_OPTIONS = {
    NAME: "Name",
    DOWNLOADS: "Number of Downloads",
    // RATINGS: "Highest Rated",
    NEWEST: "Newest",
  }

  let regionName
  if (regionFilter === "ROW") {
    regionName = ROW
  } else {
    regionName = salesforceCountries[regionFilter]
  }

  const [sortBy, setSortBy] = useState(SORT_OPTIONS.DOWNLOADS)

  // merge live data
  if (liveTemplates.data) {
    templates = templates.map(staticTemplate => {
      const liveTemplate = liveTemplates.data.find(
        t => t.sys.id === staticTemplate.contentfulId
      )

      // if the like/download/ratings data is null, set some sensible defaults
      const fields = {
        likes: liveTemplate?.fields?.likes || 0,
        downloads: liveTemplate?.fields?.downloads || 0,
        ratings: liveTemplate?.fields?.ratings || [],
      }

      return { ...staticTemplate, ...fields }
    })
  }

  //split them into 2 groups: Global and local.
  // We assume that local groups
  let localTemplatesArray = []
  let globalTemplatesArray = []

  templates.forEach(node => {
    // Region is a single so filter out any other regions (those with no region can stay)
    // if all return true

    // If theres no filter, we have no way to prioritise, so put them all in global
    if (regionFilter === "All") {
      globalTemplatesArray.push(node)
    } else {
      if (!node.region) {
        // If it has no region, put it in global
        globalTemplatesArray.push(node)
      } else if (node.region === regionName) {
        // if it has a region, and it's the right one, put it in the local array
        localTemplatesArray.push(node)
      }
    }
  })

  const sortTemplates = (a, b) => {
    const avgRating = arr => {
      if (!arr) return 0
      return arr.reduce((a, b) => parseInt(a) + parseInt(b), 0) / arr.length
    }

    switch (sortBy) {
      case SORT_OPTIONS.NAME:
        return a.title > b.title ? 1 : -1
      case SORT_OPTIONS.RATINGS:
        return avgRating(b.ratings) - avgRating(a.ratings)
      case SORT_OPTIONS.NEWEST:
        return b.createdAt - a.createdAt

      case SORT_OPTIONS.DOWNLOADS:
      default:
        return b.downloads - a.downloads
    }
  }

  localTemplatesArray.sort(sortTemplates)
  globalTemplatesArray.sort(sortTemplates)

  const localTemplatesTitle = `${
    localTemplatesArray.length
  } ${regionName} Template${localTemplatesArray.length !== 1 ? `s` : ``}`

  const globalTemplatesTitle = `${globalTemplatesArray.length}${
    globalTemplatesArray.length > 0 ? ` Global` : ``
  } Template${globalTemplatesArray.length !== 1 ? `s` : ``}`
  const genericTemplatesTitle = `${templates.length} Template${
    templates.length !== 1 ? `s` : ``
  }`

  const FilterBar = () => {
    const titleString = !!localTemplatesArray.length
      ? localTemplatesTitle
      : regionFilter === "All"
      ? genericTemplatesTitle
      : globalTemplatesTitle

    return (
      <SectionTitle>
        <H3>{titleString}</H3>
        <div>
          <strong
            css={breakpoint(
              `xs`,
              `sm`
            )(
              css`
                display: none;
              `
            )}
          >
            Sort by
          </strong>{" "}
          <Select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            css={css`
              margin: 0 0 0 8px;
              width: 200px;
            `}
          >
            {Object.keys(SORT_OPTIONS).map(optionID => (
              <option key={optionID} value={SORT_OPTIONS[optionID]}>
                {SORT_OPTIONS[optionID]}
              </option>
            ))}
          </Select>
        </div>
      </SectionTitle>
    )
  }
  const LocalTemplatesList = () => {
    if (!localTemplatesArray.length) return null

    return (
      <Margin bottom={1}>
        {localTemplatesArray.map((template, i) => (
          <Slat first={i === 0} key={template.slug} template={template} />
        ))}
      </Margin>
    )
  }
  const GlobalTemplatesList = () => {
    if (!globalTemplatesArray.length) return null
    // We only need a title for global templates if there is a bl.ock of local templates above them
    const title = !!localTemplatesArray.length ? globalTemplatesTitle : null
    return (
      <>
        {title && (
          <SectionTitle>
            <H3>{title}</H3>
          </SectionTitle>
        )}
        {globalTemplatesArray.map((template, i) => (
          <Slat first={i === 0} key={template.slug} template={template} />
        ))}
      </>
    )
  }
  const FallbackButton = () => {
    if (
      regionFilter === "All" ||
      localTemplatesArray.length ||
      globalTemplatesArray.length
    )
      return null

    return (
      <>
        <p>No templates found.</p>
        <Button
          type={"button"}
          onClick={() => {
            setRegionFilter("All")
          }}
        >
          View All Regions
        </Button>
      </>
    )
  }

  const schema = {
    "@context": "https://schema.org/",
    "@type": "ItemList",
    itemListElement: [
      [...localTemplatesArray, ...globalTemplatesArray].map((template, i) => ({
        "@type": "ListItem",
        position: i,
        item: {
          "@type": "Product",
          name: template.title,
          url: LinkResolver(template.internal.type, template.slug),
          manufacturer: template.createdBy
            ? template.createdBy.map(creator => ({
                "@type": "Corporation",
                name: creator.name,
              }))
            : null,
          image:
            template.seoImage?.OGImage?.src ||
            props.data.fallbackImage.childImageSharp.OGImage.src,
          sku: template.contentfulId,
          offers: {
            url: LinkResolver(template.internal.type, template.slug),
            price: "0",
            priceCurrency: "USD",
            businessFunction: "Add to Karbon",
          },
        },
      })),
    ],
  }

  return (
    <Layout menuTitle={props.pageContext?.menuTitle}>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <SEO
        title={props.pageContext.menuTitle}
        description={`${props.pageContext.headerTitle}, Free templates to improve your firm’s performance.`}
        pagePath={LinkResolver("TemplateList", props.pageContext.slug)}
      />
      <Padding bottom={4}>
        <Header>
          <H1
            mb={1}
            css={css`
              font-size: 32px;
            `}
          >
            {props.pageContext.headerTitle}
          </H1>
          <P mb={0}>
            Explore hundreds of free templates to improve your firm’s efficiency
            and effectiveness.
          </P>
        </Header>
        <FilterBar />
        <LocalTemplatesList />
        <GlobalTemplatesList />
        <FallbackButton />

        {templates.length === 0 && (
          <p>
            No templates found that match your filters.
            <br />
            <Link underlined to={"/"}>
              Reset filters
            </Link>
          </p>
        )}
      </Padding>
    </Layout>
  )
}

export const query = graphql`
  query ($types: [String!]) {
    fallbackImage: file(name: { eq: "fallback-image" }) {
      childImageSharp {
        OGImage: resize(width: 1100, height: 572, quality: 100) {
          src
        }
      }
    }
    allContentfulTemplate(
      filter: { type: { in: $types }, title: { ne: "__schema__" } }
      sort: { fields: title }
    ) {
      nodes {
        ...ContentfulTemplateSlatFragment
      }
    }
  }
`

export default TemplateListPage
